/**
 * AWE Scenarios Component
 * Interactive demonstrations of how the 8-week average works
 */

import React, { useState, useMemo } from 'react';
import { 
  TrendingUp, 
  AlertCircle, 
  Check, 
  X, 
  ChevronRight,
  RefreshCw,
  Info,
  Lightbulb,
} from 'lucide-react';
import { calculateAWE, formatCurrency } from '../utils/sspCalculations';
import { LOWER_EARNINGS_LIMIT, SSP_WEEKLY_RATE, NEW_EARNINGS_CAP_PERCENT } from '../data/constants';

// Pre-built scenarios
const PRESET_SCENARIOS = [
  {
    id: 'consistent',
    name: 'Consistent Worker',
    description: 'Regular 24-hour part-timer at £11.50/hr',
    earnings: [276, 276, 276, 276, 276, 276, 276, 276],
    notes: 'Consistent earner well above LEL - qualifies for full SSP under current rules.',
  },
  {
    id: 'variable',
    name: 'Variable Hours',
    description: 'Picks up extra shifts some weeks',
    earnings: [180, 95, 210, 145, 165, 0, 190, 230],
    notes: 'Variable earnings but AWE still above LEL. Zero-earning weeks still count in the average.',
  },
  {
    id: 'borderline',
    name: 'Borderline LEL',
    description: 'Low hours worker near the threshold',
    earnings: [120, 145, 110, 130, 125, 0, 140, 115],
    notes: 'Close to the LEL threshold. The 2026 changes will guarantee eligibility regardless.',
  },
  {
    id: 'below-lel',
    name: 'Below LEL',
    description: 'Student on minimal hours',
    earnings: [65, 80, 45, 70, 0, 60, 55, 75],
    notes: 'Below LEL - no SSP currently. From 2026, will receive 80% of AWE instead.',
  },
  {
    id: 'pickup-heavy',
    name: 'Pickup-Heavy Pattern',
    description: '4hr contract but picks up lots of shifts',
    earnings: [45, 180, 220, 165, 45, 195, 45, 210],
    notes: 'Contracted for few hours but picks up heavily. The 8-week average captures their true earnings.',
  },
  {
    id: 'holiday-impact',
    name: 'Holiday Week Impact',
    description: 'How paid holiday affects AWE',
    earnings: [200, 200, 200, 200, 200, 0, 200, 200],
    notes: 'Zero week (holiday/unpaid) pulls down the average. Holiday pay would count if included in these weeks.',
  },
];

export default function AWEScenarios() {
  const [selectedScenario, setSelectedScenario] = useState(PRESET_SCENARIOS[0]);
  const [customEarnings, setCustomEarnings] = useState([...PRESET_SCENARIOS[0].earnings]);
  const [isCustomMode, setIsCustomMode] = useState(false);
  
  const earnings = isCustomMode ? customEarnings : selectedScenario.earnings;
  
  // Calculate results
  const results = useMemo(() => {
    const awe = calculateAWE(earnings);
    
    // Calculate what they'd get under 2026 rules
    let rate2026 = SSP_WEEKLY_RATE;
    let rateReason = 'Standard rate';
    
    if (awe.awe < LOWER_EARNINGS_LIMIT) {
      const eightyPercent = awe.awe * NEW_EARNINGS_CAP_PERCENT;
      rate2026 = Math.min(eightyPercent, SSP_WEEKLY_RATE);
      rateReason = `80% of AWE (capped)`;
    }
    
    return {
      ...awe,
      rate2026,
      rateReason,
      dailyRate2026: rate2026 / 5, // Assuming 5 QDs
    };
  }, [earnings]);
  
  const handleEarningChange = (index, value) => {
    const newEarnings = [...customEarnings];
    newEarnings[index] = parseFloat(value) || 0;
    setCustomEarnings(newEarnings);
    setIsCustomMode(true);
  };
  
  const handleScenarioSelect = (scenario) => {
    setSelectedScenario(scenario);
    setCustomEarnings([...scenario.earnings]);
    setIsCustomMode(false);
  };
  
  const handleReset = () => {
    setCustomEarnings([...selectedScenario.earnings]);
    setIsCustomMode(false);
  };
  
  return (
    <div className="space-y-6">
      {/* Intro */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-6 text-white">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-white/20 rounded-lg">
            <TrendingUp className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-display font-semibold mb-2">
              Understanding the 8-Week Average
            </h2>
            <p className="text-primary-100">
              SSP eligibility depends on Average Weekly Earnings (AWE) over the 8 weeks 
              before sickness. Explore how different earning patterns affect eligibility 
              and what changes in April 2026.
            </p>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Scenarios */}
        <div className="space-y-4">
          <h3 className="font-semibold text-slate-900">Scenarios</h3>
          <div className="space-y-2">
            {PRESET_SCENARIOS.map((scenario) => (
              <button
                key={scenario.id}
                onClick={() => handleScenarioSelect(scenario)}
                className={`w-full text-left p-3 rounded-lg border transition-all ${
                  selectedScenario.id === scenario.id && !isCustomMode
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">{scenario.name}</p>
                    <p className="text-xs text-slate-500">{scenario.description}</p>
                  </div>
                  <ChevronRight className={`w-5 h-5 transition-transform ${
                    selectedScenario.id === scenario.id && !isCustomMode ? 'text-primary-500' : 'text-slate-300'
                  }`} />
                </div>
              </button>
            ))}
          </div>
          
          {isCustomMode && (
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-amber-800">Custom values</span>
                <button onClick={handleReset} className="text-xs text-amber-600 hover:text-amber-800 flex items-center gap-1">
                  <RefreshCw className="w-3 h-3" />
                  Reset
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Middle: Interactive Calculator */}
        <div className="lg:col-span-2 space-y-6">
          {/* 8-Week Input Grid */}
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-slate-900">8-Week Earnings</h3>
              <p className="text-xs text-slate-500 mt-0.5">Click any value to modify it</p>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
                {earnings.map((earning, index) => (
                  <div key={index} className="text-center">
                    <div className="text-xs text-slate-500 mb-1">Week {index + 1}</div>
                    <input
                      type="number"
                      value={earning}
                      onChange={(e) => handleEarningChange(index, e.target.value)}
                      className={`w-full text-center font-mono p-2 rounded-lg border ${
                        earning === 0 
                          ? 'bg-red-50 border-red-200 text-red-700' 
                          : earning < LOWER_EARNINGS_LIMIT / 1.5
                            ? 'bg-amber-50 border-amber-200'
                            : 'bg-green-50 border-green-200'
                      }`}
                    />
                  </div>
                ))}
              </div>
              
              {/* Visual bar chart */}
              <div className="mt-6 h-32 flex items-end gap-1.5">
                {earnings.map((earning, index) => {
                  const maxEarning = Math.max(...earnings, 300);
                  const height = maxEarning > 0 ? (earning / maxEarning) * 100 : 0;
                  const lelLine = (LOWER_EARNINGS_LIMIT / maxEarning) * 100;
                  
                  return (
                    <div key={index} className="flex-1 flex flex-col items-center">
                      <div className="w-full bg-slate-100 rounded-t relative" style={{ height: '100%' }}>
                        <div 
                          className={`absolute bottom-0 w-full rounded-t transition-all ${
                            earning === 0 ? 'bg-red-400' : 'bg-primary-500'
                          }`}
                          style={{ height: `${height}%` }}
                        />
                        {/* LEL reference line */}
                        <div 
                          className="absolute w-full border-t-2 border-dashed border-amber-500"
                          style={{ bottom: `${lelLine}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-end gap-2 mt-2 text-xs text-slate-500">
                <div className="w-4 h-0.5 bg-amber-500 border-t-2 border-dashed border-amber-500" />
                <span>LEL (£{LOWER_EARNINGS_LIMIT})</span>
              </div>
            </div>
          </div>
          
          {/* Results */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* AWE Calculation */}
            <div className="card">
              <div className="card-body">
                <h4 className="text-sm font-medium text-slate-600 mb-3">AWE Calculation</h4>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Total (8 weeks)</span>
                    <span className="font-mono">{formatCurrency(results.total)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">÷ 8 weeks</span>
                    <span className="font-mono">8</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-slate-200 font-semibold">
                    <span>AWE</span>
                    <span className="font-mono text-lg">{formatCurrency(results.awe)}</span>
                  </div>
                </div>
                
                <div className={`mt-4 p-3 rounded-lg ${
                  results.meetsLEL ? 'bg-green-50' : 'bg-amber-50'
                }`}>
                  <div className="flex items-center gap-2">
                    {results.meetsLEL ? (
                      <Check className="w-5 h-5 text-green-600" />
                    ) : (
                      <X className="w-5 h-5 text-amber-600" />
                    )}
                    <span className={`text-sm font-medium ${
                      results.meetsLEL ? 'text-green-800' : 'text-amber-800'
                    }`}>
                      {results.meetsLEL 
                        ? 'Above LEL - Eligible for SSP' 
                        : `Below LEL by ${formatCurrency(results.lelShortfall)}`
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* SSP Rates */}
            <div className="card">
              <div className="card-body">
                <h4 className="text-sm font-medium text-slate-600 mb-3">SSP Entitlement</h4>
                
                <div className="space-y-3">
                  {/* Current */}
                  <div className="p-3 bg-primary-50 rounded-lg">
                    <div className="text-xs text-primary-600 font-medium mb-1">Current Rules</div>
                    {results.meetsLEL ? (
                      <div className="font-mono text-primary-900">
                        <span className="text-lg font-bold">{formatCurrency(SSP_WEEKLY_RATE)}</span>
                        <span className="text-sm">/week</span>
                      </div>
                    ) : (
                      <div className="text-primary-700">Not eligible</div>
                    )}
                  </div>
                  
                  {/* 2026 */}
                  <div className="p-3 bg-accent-50 rounded-lg">
                    <div className="text-xs text-accent-600 font-medium mb-1">April 2026 Rules</div>
                    <div className="font-mono text-accent-900">
                      <span className="text-lg font-bold">{formatCurrency(results.rate2026)}</span>
                      <span className="text-sm">/week</span>
                    </div>
                    {results.awe < LOWER_EARNINGS_LIMIT && (
                      <div className="text-xs text-accent-600 mt-1">
                        (80% of £{results.awe.toFixed(2)} AWE)
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Scenario Notes */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-blue-900 mb-1">
                  {isCustomMode ? 'Custom Scenario' : selectedScenario.name}
                </h4>
                <p className="text-sm text-blue-800">
                  {isCustomMode 
                    ? 'You\'ve modified the earnings. The AWE is recalculated automatically.'
                    : selectedScenario.notes
                  }
                </p>
              </div>
            </div>
          </div>
          
          {/* Key Learnings */}
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-slate-900">Key Points</h3>
            </div>
            <div className="card-body">
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-2" />
                  <span>
                    <strong>Zero weeks count:</strong> Weeks with no earnings still count as £0 in the average, 
                    pulling down AWE.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-2" />
                  <span>
                    <strong>8 weeks before sickness:</strong> The calculation uses the 8 weeks ending with 
                    the last payday before the illness started.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-2" />
                  <span>
                    <strong>Variable patterns:</strong> For shift workers, the 8-week average captures 
                    their true earnings better than contracted hours alone.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent-500 mt-2" />
                  <span>
                    <strong>2026 change:</strong> The LEL threshold is removed. Everyone qualifies, but 
                    lower earners get 80% of their AWE (capped at the standard rate).
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
